// Warp Text dialog — Photoshop's Type > Warp Text dialog clone.
// Style dropdown + Horizontal/Vertical orientation + Bend / Horizontal Distortion / Vertical Distortion sliders + Cancel/OK.
//
// Open from the host UI (right-click on a Type layer → Warp Text), pass the
// current TypeLayer.warp params and an onApply callback that receives the new
// params.

import type { WarpTextParams, WarpTextStyle } from "../../core/document/TypeLayer";
import { DEFAULT_WARP, isTypeLayer } from "../../core/document/TypeLayer";
import { findLayer } from "../../core/document/LayerUtils";
import type { EditorState } from "../state/EditorState";
import { trapFocus } from "./ColorPicker";

export interface WarpTextDialogOptions {
  initial?: WarpTextParams;
  onApply: (params: WarpTextParams) => void;
  onCancel?: () => void;
}

const WARP_STYLES: Array<{ value: WarpTextStyle; label: string }> = [
  { value: "none",        label: "None" },
  { value: "arc",         label: "Arc" },
  { value: "arc-lower",   label: "Arc Lower" },
  { value: "arc-upper",   label: "Arc Upper" },
  { value: "arch",        label: "Arch" },
  { value: "bulge",       label: "Bulge" },
  { value: "shell-lower", label: "Shell Lower" },
  { value: "shell-upper", label: "Shell Upper" },
  { value: "flag",        label: "Flag" },
  { value: "wave",        label: "Wave" },
  { value: "fish",        label: "Fish" },
  { value: "rise",        label: "Rise" },
  { value: "fisheye",     label: "Fisheye" },
  { value: "inflate",     label: "Inflate" },
  { value: "squeeze",     label: "Squeeze" },
  { value: "twist",       label: "Twist" },
];

export class WarpTextDialog {
  private container: HTMLElement;
  private params: WarpTextParams;
  private opts: WarpTextDialogOptions;

  constructor(opts: WarpTextDialogOptions) {
    this.opts = opts;
    this.params = { ...DEFAULT_WARP, ...(opts.initial ?? {}) };
    this.container = document.createElement("div");
    this.container.className = "warp-text-dialog";
    // ARIA: modal dialog accessibility (#47)
    this.container.setAttribute("role", "dialog");
    this.container.setAttribute("aria-modal", "true");
    this.container.setAttribute("aria-labelledby", "warp-text-dialog-title");
    this._buildUI();
  }

  private _buildUI(): void {
    this.container.innerHTML = `
      <div class="panel-title" id="warp-text-dialog-title">Warp Text</div>
      <div class="warp-text-dialog__content">
        <label class="brush-row">
          Style
          <select class="warp-style">
            ${WARP_STYLES.map(s => `<option value="${s.value}" ${this.params.style === s.value ? "selected" : ""}>${s.label}</option>`).join("")}
          </select>
        </label>
        <div class="brush-row warp-orientation">
          <label><input type="radio" name="warp-orient" value="horizontal" ${this.params.orientation === "horizontal" ? "checked" : ""}> Horizontal</label>
          <label><input type="radio" name="warp-orient" value="vertical" ${this.params.orientation === "vertical" ? "checked" : ""}> Vertical</label>
        </div>
        <label class="brush-row">
          Bend
          <input type="range" class="warp-bend" min="-100" max="100" value="${Math.round(this.params.bend * 100)}">
          <span class="warp-bend-val">${Math.round(this.params.bend * 100)}%</span>
        </label>
        <label class="brush-row">
          Horizontal Distortion
          <input type="range" class="warp-hdist" min="-100" max="100" value="${Math.round(this.params.horizontalDistortion * 100)}">
          <span class="warp-hdist-val">${Math.round(this.params.horizontalDistortion * 100)}%</span>
        </label>
        <label class="brush-row">
          Vertical Distortion
          <input type="range" class="warp-vdist" min="-100" max="100" value="${Math.round(this.params.verticalDistortion * 100)}">
          <span class="warp-vdist-val">${Math.round(this.params.verticalDistortion * 100)}%</span>
        </label>
        <div class="warp-text-dialog__buttons">
          <button class="btn-secondary warp-cancel">Cancel</button>
          <button class="btn-primary warp-ok">OK</button>
        </div>
      </div>
    `;
    this._bindEvents();
  }

  private _bindEvents(): void {
    const styleSel = this.container.querySelector(".warp-style") as HTMLSelectElement | null;
    styleSel?.addEventListener("change", () => {
      this.params = { ...this.params, style: styleSel.value as WarpTextStyle };
    });

    const orientationRadios = this.container.querySelectorAll<HTMLInputElement>("input[name=warp-orient]");
    orientationRadios.forEach(r => {
      r.addEventListener("change", () => {
        if (r.checked) {
          this.params = { ...this.params, orientation: r.value as "horizontal" | "vertical" };
        }
      });
    });

    const bend = this.container.querySelector(".warp-bend") as HTMLInputElement | null;
    const bendVal = this.container.querySelector(".warp-bend-val") as HTMLElement | null;
    bend?.addEventListener("input", () => {
      const pct = parseInt(bend.value);
      this.params = { ...this.params, bend: pct / 100 };
      if (bendVal) bendVal.textContent = `${pct}%`;
    });

    const hd = this.container.querySelector(".warp-hdist") as HTMLInputElement | null;
    const hdVal = this.container.querySelector(".warp-hdist-val") as HTMLElement | null;
    hd?.addEventListener("input", () => {
      const pct = parseInt(hd.value);
      this.params = { ...this.params, horizontalDistortion: pct / 100 };
      if (hdVal) hdVal.textContent = `${pct}%`;
    });

    const vd = this.container.querySelector(".warp-vdist") as HTMLInputElement | null;
    const vdVal = this.container.querySelector(".warp-vdist-val") as HTMLElement | null;
    vd?.addEventListener("input", () => {
      const pct = parseInt(vd.value);
      this.params = { ...this.params, verticalDistortion: pct / 100 };
      if (vdVal) vdVal.textContent = `${pct}%`;
    });

    const ok = this.container.querySelector(".warp-ok") as HTMLButtonElement | null;
    ok?.addEventListener("click", () => {
      this.opts.onApply({ ...this.params });
      this.destroy();
    });

    const cancel = this.container.querySelector(".warp-cancel") as HTMLButtonElement | null;
    cancel?.addEventListener("click", () => {
      this.opts.onCancel?.();
      this.destroy();
    });
  }

  /** Returns the params currently shown in the dialog. For tests. */
  getParams(): WarpTextParams {
    return { ...this.params };
  }

  /** Returns the dialog element. */
  getElement(): HTMLElement {
    return this.container;
  }

  /** List of style options shown in the dropdown — useful for tests. */
  static getAllStyles(): Array<{ value: WarpTextStyle; label: string }> {
    return WARP_STYLES.slice();
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }
}

// ── Host wiring (i14) ────────────────────────────────────────────────────
//
// This dialog is presentation-only (mount/destroy + onApply/onCancel); it
// doesn't know about EditorState, layers, or Commands. The two host entry
// points — Layer > Type > Warp Text… (src/main.ts) and the Type options
// bar's Warp button (src/app/ui/ToolUX.ts) — both need the exact same
// behavior: find the active type layer, no-op if there isn't one, seed the
// dialog with its current `warp`, and on Apply hand the new params back so
// the caller can dispatch `SetLayerPropertyCommand` through `state.execute`
// (undoable, triggers a repaint via EditorState.notify()). Rather than
// duplicate that lookup + overlay/close plumbing in both call sites, it's
// centralized here — mirrors the overlay/Escape/click-outside/focus-restore
// convention `openLayerStyleDialog` (LayerStyleDialog.ts) already uses.

/**
 * Resolve the active type layer for the Warp Text dialog's entry points.
 * Returns null when there's no active layer, or the active layer isn't a
 * type layer — callers use this to no-op instead of opening a broken dialog.
 */
export function getActiveTypeLayerWarp(
  state: EditorState | null | undefined,
): { id: string; warp: WarpTextParams | undefined } | null {
  const doc = state?.doc;
  if (!doc?.activeLayerId) return null;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer || !isTypeLayer(layer)) return null;
  return { id: layer.id, warp: layer.warp };
}

export interface WarpTextDialogHostDeps {
  /** Returns the active type layer's id + current warp, or null if there is
   * none — both entry points must no-op in that case (no dialog opens). */
  getActiveTypeLayer: () => { id: string; warp: WarpTextParams | undefined } | null;
  /** Invoked with the new params when the user clicks OK. The host is
   * responsible for dispatching `SetLayerPropertyCommand(layerId, { warp })`
   * via `state.execute` so the change is undoable and repaints. */
  onApply: (layerId: string, warp: WarpTextParams) => void;
}

/**
 * Open the Warp Text dialog for the current active type layer, wrapped in a
 * modal overlay (backdrop, Escape-to-close, click-outside-to-close, Tab
 * focus trap, and focus restore on close). No-ops when there is no active
 * type layer. Shared by both host entry points so they behave identically.
 */
export function openWarpTextDialogForActiveLayer(deps: WarpTextDialogHostDeps): void {
  const layer = deps.getActiveTypeLayer();
  if (!layer) return;

  const openerEl = document.activeElement as HTMLElement | null;

  const overlay = document.createElement("div");
  overlay.style.cssText =
    "position:fixed;top:0;left:0;right:0;bottom:0;" +
    "background:rgba(0,0,0,0.6);display:flex;align-items:center;" +
    "justify-content:center;z-index:10000;";

  const dialog = new WarpTextDialog({
    // exactOptionalPropertyTypes: only set `initial` when there's a value —
    // WarpTextDialogOptions.initial is optional-and-absent, not optional-
    // and-undefined.
    ...(layer.warp !== undefined ? { initial: layer.warp } : {}),
    onApply: (warp) => {
      deps.onApply(layer.id, warp);
      close();
    },
    onCancel: () => {
      close();
    },
  });

  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      close();
      return;
    }
    trapFocus(e, dialog.getElement());
  }

  function close(): void {
    dialog.destroy();
    overlay.remove();
    document.removeEventListener("keydown", onKey);
    openerEl?.focus?.();
  }

  dialog.mount(overlay);
  document.body.appendChild(overlay);

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener("keydown", onKey);

  const firstFocusable = dialog.getElement().querySelector<HTMLElement>(
    "button, input, select, textarea, [tabindex]:not([tabindex='-1'])",
  );
  firstFocusable?.focus();
}
